import React, {useState, useEffect} from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import PropTypes from 'prop-types';
import {toTime, toTimeType, timeConverter, isObjectEmpty, uploadCSV, replaceAll, upperClamp, timeConverterSM} from '../../../Utils';
import Spinner from '../../common/generic/Spinner';

const ContainerComp = ({pageState, reportState, csvData, setcsvData}) =>{
    const {database, deleteThis, request, fetchModules, modulesData, modulesReady } = pageState;
    const [processedMarks, setProcessedMarks] = useState({});
    const [markRecords, setMarkRecords] = useState({});
    const {selectedStudents, selectedProgram} = reportState;

    /*
    CSV NOTES
    =========
    There is a big problem when trying to upload more then 1 csv file.  The below system will reset all the data in 
    this component and start it over when when the user clicks save.
    
    Steps
    1) User clicks save in either ProgramReportTableComp or AcitivityReportComp
        1b) openCSVPage() will populate csvData using setcsvData({processStage:'newRequest'});
    2) ViewReportsPage checks to see if the marks have changed (* seems to be called way to many times...)
        2b) setReportType(csvData.reportType);
            setSelectedProgram({...csvData.selectedProgram});
            setSelectedStudents([...csvData.selectedStudents]); 
    3) Start the report
        3a) CSV Step 1: checks if a new request for csv and starts to clear the data
        3b) CSV Step 2: clears the data 
        3c) CSV Step 3: keep checking to see when the data has been cleared
        3d) CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
        3e) (The normal Startup starts at CSV Step 1) 
    */

    // CSV Step 4: once the data for this component has been reset then start the normal start up process for this comp.
    // The normal Startup starts at CSV Step 1 
    const tryLoadingCSVStage2 = () => {
        if(!csvData) return;
        if(csvData.processStage === 'clearingData'){
            if(!database['getProgramMarks'] && isObjectEmpty(markRecords) && isObjectEmpty(processedMarks)){
                setcsvData({...csvData, processStage:'clearedData'});
            }
        }
    };
    // CSV Step 3: keep checking to see when the data has been cleared
    useEffect(() =>{tryLoadingCSVStage2();},[database['getProgramMarks']]);
    useEffect(() =>{tryLoadingCSVStage2();},[markRecords]);
    useEffect(() =>{tryLoadingCSVStage2();},[processedMarks]);

    // CSV Step 2: clears the data
    useEffect(() => {
        if(!csvData) return;

        if(csvData.processStage === 'clearingData'){
            deleteThis('getProgramMarks');
            setMarkRecords({});
            setProcessedMarks({});
        }
    },[csvData]);

    // CSV Step 1: checks if a new request for csv and starts to clear the data 
    // Normal Startup Step 1:
    useEffect(() => {
        if(csvData){
            if(csvData.processStage==='newRequest'){
                // csvData.setloadingCSV({...csvData.loadingCSV, unit:true});
                setcsvData({...csvData, processStage:'clearingData'});
                return;
            }

            if(csvData.processStage==='clearingData') return;
        }

        if(!isObjectEmpty(markRecords)) return; // already loading

        if(!database['getProgramMarks']){
            let studentQueries = selectedStudents.map(studentRec=>{
                let getMarksQuery={
                    params: studentRec.id+'~'+selectedProgram.id.toLowerCase(),
                    func:'getProgramMarks'            
                };
                return getMarksQuery;
            });

            if(studentQueries.length > 0){
                let counter = 1;
                studentQueries = studentQueries.map((query,i)=>{
                    let prevQuery = (i!=0) ? studentQueries[i-1] : 'undefinded';
                    if(prevQuery !== 'undefinded'){
                        counter++;
                        query.nextRequest = prevQuery;
                    }
                    return query;
                });
                let loadChainedMarks = studentQueries[studentQueries.length-1];
                loadChainedMarks.numQueries = counter;
                setMarkRecords({info:{numStudents:counter,numLoaded:counter}});

                // pass the link list of saved changes into the database
                request(loadChainedMarks); 
            }
            else
                console.log('Nothing to load?');
        }
    });

    // convert database['getProgramMarks'] into setMarkRecords
    // UDATES - when database['getProgramMarks'] changes
    useEffect(() => {
        if(csvData && csvData.processStage !== 'clearedData') return; // don't setup markRecords for csv files without being ready 
        if(isObjectEmpty(markRecords)) return;
        (!pageState.isFetchingModules && !modulesReady) && fetchModules(selectedProgram.id.toLowerCase());
        if(!database['getProgramMarks']) return;
        
        let marks = {};
        let key = 'unavailable';
        let counter = {...markRecords.info};

        if(database['getProgramMarks'].isLoaded) counter.numLoaded--;
        
        marks = {
            isloaded: database['getProgramMarks'].isLoaded,
            data: database['getProgramMarks'].data
        }; 

        if((marks.data) && !isObjectEmpty(marks.data)){
            key = Object.keys(marks.data)[0].split('+')[0];
        }

        setMarkRecords({...markRecords, info:{...counter}, [key]:marks});

    }, [database['getProgramMarks']]); 

    const tryLoadingNow = () => {
        if(isObjectEmpty(markRecords)) return;
        if(!isObjectEmpty(processedMarks)) return;
        if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return;

        let reportData = JSON.parse(localStorage.getItem('reportData'));
        let masterLevel = parseInt(reportData.masterLevel)/100;

        if(modulesReady) { 
            let studentsModuleMarks = selectedStudents.reduce((acc,studentRec)=>{
                let studentID = studentRec.id;
                let studentMarkRecords = markRecords[studentID];
                if(!studentMarkRecords) return acc; // still loading marks

                let marksPerModule = modulesData.map(moduleRec => { // foreach module
                    let moduleID = moduleRec.moduleID;
                    return moduleRec.units.map(unitRec => // foreach unit in a module attach marks to it.
                    {
                        let numActs = unitRec.actIDs.length;
                        return unitRec.actIDs.reduce((acc, actID)=>{
                            if(studentMarkRecords.data[`${studentID}+${moduleID[0]}+${actID}`]){
                                if(acc.hasMark){
                                    let mark = studentMarkRecords.data[`${studentID}+${moduleID[0]}+${actID}`];
                                    acc.mark = {
                                        CumulativeTime: parseInt(acc.mark.CumulativeTime) + parseInt(mark.CumulativeTime), 
                                        QuestionsAttempted: parseInt(acc.mark.QuestionsAttempted) + parseInt(mark.QuestionsAttempted), 
                                        QuestionsRight: parseInt(acc.mark.QuestionsRight) + parseInt(mark.QuestionsRight), 
                                        TimesCompleted: parseInt(acc.mark.TimesCompleted) + upperClamp(1)(parseInt(mark.TimesCompleted)) 
                                    };
                                }
                                else{
                                    let mark = studentMarkRecords.data[`${studentID}+${moduleID[0]}+${actID}`];

                                    acc.mark = {
                                        CumulativeTime: parseInt(mark.CumulativeTime), 
                                        QuestionsAttempted: parseInt(mark.QuestionsAttempted), 
                                        QuestionsRight: parseInt(mark.QuestionsRight), 
                                        TimesCompleted: upperClamp(1)(parseInt(mark.TimesCompleted)), 
                                    };
                                    
                                    acc.hasMark = true;
                                }
                            }
                            return acc;
                        },{hasMark: false, moduleID, linkID:unitRec.linkID, name:unitRec.name, numActs, mark:{CumulativeTime:'--', QuestionsAttempted:'--', QuestionsRight:'--', TimesCompleted:'--'}});
                    }).filter(e=> e); // remove empty records (ones with no marks)
                });

                acc[`${studentRec.id}+${studentRec.name}`] = marksPerModule;
                return acc;
            },{});
            setProcessedMarks(studentsModuleMarks);

            if(csvData){

                let newLine = '%0A';
                let csvString = '';
                csvString =`"* The Mastery Level determines what percentage a student must achieve for an activity to earn a gold star (master it). The default Mastery Level is ${masterLevel}%. You can change this value in the Manage Classes tab."${newLine}`;
                csvString +=`"Students without marks will not appear in this report"${newLine}`;
                let tableHeaderStr = '"Unit","Number of Mastered*","Activities in Unit","Questions Correct","Questions Attempted","Time in Unit"'+newLine;
              
                Object.keys(studentsModuleMarks).forEach((key) => { 
                    let headerStr = `${newLine}"${key.split('+')[1]}"${newLine}"${selectedProgram.name}"${newLine}"Unit Report"${newLine}`;
                    let tableData  = studentsModuleMarks[key];

                    csvString += headerStr + tableHeaderStr;
                    tableData.forEach((moduleRec) => {
                        let moduleTitleStr = (moduleRec.length > 0) ? `"${moduleRec[0].moduleID[1]}"${newLine}` : '';
                        csvString += moduleTitleStr;
                        moduleRec.forEach((markRec) => {
                            const { name, numActs, mark} = markRec;
                            const { TimesCompleted, QuestionsRight, QuestionsAttempted, CumulativeTime } = mark;
                            let theTime = !isNaN(CumulativeTime) ? `${toTime(CumulativeTime)} ${toTimeType(CumulativeTime)}`: '--';
                            let recordStr = `"${name}","${TimesCompleted}","${numActs}","${QuestionsRight}","${QuestionsAttempted}","${theTime}"${newLine}`;
                            csvString += recordStr;
                        });
                    });
                });
                            
                let name = '';
                if(Object.keys(studentsModuleMarks).length===0){
                    window.alert('There are no marks for the student(s) selected');
                    csvData.setloadingCSV({...csvData.loadingCSV, unit:false});
                    return;
                }
                if(Object.keys(studentsModuleMarks).length==1)
                    name = Object.keys(studentsModuleMarks)[0].split('+')[1]+'_';

                let today = new Date();
                today = timeConverterSM(today.getTime());

                uploadCSV(name+'UnitReport_'+selectedProgram.name+'_'+today,csvString);
                //uploadCSV('unitReport',csvString);
                csvData.setloadingCSV({...csvData.loadingCSV, unit:false});
            }
        };
    }; 
    useEffect(() =>{tryLoadingNow();},[modulesData]);
    useEffect(() =>{tryLoadingNow();},[markRecords]);

    if(csvData) return <></>;
    if(isObjectEmpty(markRecords)) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;
    if(markRecords.info.numStudents > 0 && markRecords.info.numLoaded > 0) return <> <Spinner/> <center>Loading... ({database['queryCount']})</center> </>;

    return <>
        {Object.keys(processedMarks).length < selectedStudents.length && <center> <p>  Students without marks will not appear in this report </p></center>}
        {Object.keys(processedMarks).map((key, i) => ( 
            <span key={i}><br/>
            <UnitReportComp header={{student: key.split('+')[1], program: selectedProgram.name}} tableData={processedMarks[key]}/>            
            <br/></span>
        ))}
    </>;
};

const UnitReportComp = ({header, tableData}) => {
    const [masterLevel, setMasterLevel] = useState(0.8);
    const [totals, setTotals] = useState({
        name:'',
        numActs:0,
        haveTotals:false,
        mark:{
            TimesCompleted:0,
            numActs:0,
            QuestionsRight:0,
            QuestionsAttempted:0,
            CumulativeTime:0
        }
    });

    useEffect(()=>{
        let reportData = JSON.parse(localStorage.getItem('reportData'));
        if(masterLevel !== (parseInt(reportData.masterLevel)/100))
            setMasterLevel(parseInt(reportData.masterLevel)/100);
        
        if(totals.haveTotals)
            return;

        let tmpTotals = {
            name:'',
            numActs:0,
            haveTotals:false,
            mark:{
                TimesCompleted:0,
                numActs:0,
                QuestionsRight:0,
                QuestionsAttempted:0,
                CumulativeTime:0
            }
        };

        tableData.map((moduleRec) => {
            moduleRec.map((markRec) => {
                tmpTotals.numActs+= Number(markRec.numActs); 
                tmpTotals.mark.TimesCompleted+=markRec.mark.TimesCompleted !== '--'?Number(markRec.mark.TimesCompleted):0; 
                tmpTotals.mark.QuestionsRight+=markRec.mark.QuestionsRight !== '--'?Number(markRec.mark.QuestionsRight):0;
                tmpTotals.mark.QuestionsAttempted+=markRec.mark.QuestionsAttempted !== '--'?Number(markRec.mark.QuestionsAttempted):0;
                tmpTotals.mark.CumulativeTime+=markRec.mark.CumulativeTime !== '--'?Number(markRec.mark.CumulativeTime):0;
            });
        });
        tmpTotals.haveTotals = true;
        setTotals(tmpTotals);
    });

    const titleRow = <Row style={{ fontSize:'0.9em', fontWeight:'bold'}}>
    <Col xs='5'><Row>
        <Col style={{textAlign: 'left'}} xs='12'> Unit </Col>
        </Row>
    </Col>
    <Col xs='7'><Row>
        <Col style={{textAlign: 'center'}} xs='4'>Number of Mastered*</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Activities in Unit</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Questions Correct</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Questions Attempted</Col>
        <Col style={{textAlign: 'center'}} xs='2'>Time in Unit</Col>
        </Row></Col>
    </Row>;

    const headerComp = <center>
        <h4><Row>
            <Col xs='4'>{header.student}</Col> 
            <Col xs='4'>{header.program} </Col>
            <Col xs='4'>Unit Report</Col>
        </Row></h4>
    </center>;

    const Footer = ()=> <center><hr/>* The Mastery Level determines what percentage a student must achieve for an activity to earn a gold star (master it). The default Mastery Level is {masterLevel*100}%. You can change this value in the Manage Classes tab.</center>;

    return (
        <>
            {headerComp}
            <hr/>
            {titleRow}
            {tableData.map((moduleRec, module_i) => <span key={module_i}>
                {(moduleRec.length > 0) && <Row><Col xs='12' style={{fontWeight:'bold'}} >{moduleRec[0].moduleID[1]}</Col></Row>}
                {moduleRec.map((markRec, mark_i) => 
                    <TableRow key={mark_i} data={markRec} fontWeight={'normal'}/>
                )}
                </span>
            )}
            <TableRow data={totals} fontWeight={'bold'}/>
            <Footer/>
        </>
    );
};
UnitReportComp.propTypes = {
    tableData: PropTypes.array
};

const TableRow = (props) => {
    const { moduleID, linkID, name, numActs, mark} = props.data;
    const { TimesCompleted, QuestionsRight, QuestionsAttempted, CumulativeTime } = mark;

    return (
        <Row style={{fontSize:'0.9em'}}>
            <Col xs='5'><Row>
                <Col style={{textAlign: 'left', fontWeight: props.fontWeight}} xs='12'> {name} </Col>
                </Row>
            </Col>
            <Col xs='7'><Row>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='4'>{TimesCompleted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{numActs}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{QuestionsRight}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{QuestionsAttempted}</Col>
                <Col style={{textAlign: 'center', fontWeight: props.fontWeight}} xs='2'>{ !isNaN(CumulativeTime) ? <>{toTime(CumulativeTime)} {toTimeType(CumulativeTime)}</> : '--'}</Col>
            </Row></Col>
        </Row>
    );
};

TableRow.propTypes = {
    fontWeight: PropTypes.string,
    data: PropTypes.object
};

export default ContainerComp;